import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { AbstractService } from 'src/app/services/abstract.service';
import { DicionarioSchema, ImportacaoJrxml, RelatorioDinamicoService } from 'src/app/services/relatorio-dinamico.service';

const NOMES_TOTAL: { [k: string]: string } = {
  soma: 'Soma',
  media: 'Média',
  contagem: 'Contagem',
  distintos: 'Distintos (conta sem repetir)',
  min: 'Mínimo',
  max: 'Máximo',
  grupo: 'Soma, uma vez por grupo',
};

const SQL_INICIAL = `select v.id,
       v.dataemissao as emissao,
       v.totais_total as total
  from venda v
 where v.deleted = false
   and v.idempresa = any(:empresas)
   and v.dataemissao >= :periodo_inicio
   and v.dataemissao < :periodo_fim`;

@Component({
  selector: 'habil-fonte-sql',
  templateUrl: './fonte-sql.component.html',
  styleUrls: ['./fonte-sql.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class FonteSqlComponent implements OnInit {
  id: number = null;
  podeSql = false;
  podeExcluir = false;
  carregando = false;
  ocupado = '';

  fonte: any = this.fonteVazia();
  versaoAtual: number = null;
  temRascunho = false;
  modelos: any[] = [];

  validacao: { erros: string[]; avisos: string[] } = null;
  avisosImportacao: string[] = [];
  origemImportacao = '';
  sugestaoModelo: any = null;
  variaveisAbertas = new Set<any>();
  previa: any = null;
  plano: any = null;
  colunasPrevia: string[] = [];
  porPaginaPrevia = 50;
  primeiraPrevia = 0;

  valoresPrevia: { [chave: string]: any } = {};
  alturaBancada = 'calc(100vh - 150px)';
  abaInspetor = 0;
  abaConsole = 0;
  colunasAbertas = new Set<any>();

  mostrarHistorico = false;
  versoes: any[] = [];
  versaoComparada: any = null;

  mostrarSalvarVersao = false;
  notasVersao = '';

  rotinas: { label: string; value: string }[] = [];
  dicionario: DicionarioSchema = null;
  tiposParametro = ['periodo', 'data', 'inteiro', 'decimal', 'texto', 'booleano', 'listaInteiro', 'listaTexto', 'empresas'].map((t) => ({ label: t, value: t }));
  calculosVariavel = Object.keys(NOMES_TOTAL).map((k) => ({ label: NOMES_TOTAL[k], value: k }));
  tiposColuna = ['texto', 'inteiro', 'decimal', 'moeda', 'percentual', 'data', 'dataHora', 'booleano'].map((t) => ({ label: t, value: t }));
  padroesPeriodo = ['mesAtual', 'mesAnterior', 'hoje', 'ontem', 'semanaAtual', 'ultimos7', 'ultimos30', 'anoAtual'].map((t) => ({ label: t, value: t }));
  softwares = ['HABIL_10', 'LIMBER_CHEF', 'HABIL_EMPRESARIAL', 'HABIL_PLAY', 'HABIL_SPA', 'HABIL_FRANQUEADORA'].map((t) => ({ label: t, value: t }));

  constructor(
    public service: RelatorioDinamicoService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private breadcrumbService: BreadcrumbService,
    titleService: Title
  ) {
    titleService.setTitle('Fonte SQL - ' + AbstractService.app);
  }

  ngOnInit(): void {
    this.podeSql = this.service.pode('S');
    this.podeExcluir = this.service.pode('D');
    this.breadcrumb();
    if (!this.podeSql) {
      return;
    }
    this.service.rotinas().subscribe((r) => (this.rotinas = r.map((x) => ({ label: `${x.description} (${x.value})`, value: x.value }))));
    this.service.dicionario().subscribe(
      (d) => (this.dicionario = d),
      () => undefined
    );
    this.route.queryParams.subscribe((p) => {
      if (p.id && p.id !== 'nova') {
        this.carregar(Number(p.id));
      } else {
        this.id = null;
        const importacao = this.service.importacaoPendente;
        this.service.importacaoPendente = null;
        if (importacao) {
          this.fonte = this.normalizar(importacao.fonte);
          this.mostrarImportacao(importacao);
        } else {
          this.fonte = this.fonteVazia();
        }
      }
    });
  }

  @ViewChild('raiz') set raiz(ref: ElementRef<HTMLDivElement>) {
    this.elementoRaiz = ref?.nativeElement;
    if (this.elementoRaiz) {
      setTimeout(() => this.ajustarAltura());
    }
  }

  private elementoRaiz: HTMLDivElement;

  @ViewChild('bancada') bancada: ElementRef<HTMLDivElement>;

  readonly LARGURA_INSPETOR_PADRAO = 460;
  larguraInspetor = this.larguraSalva();
  arrastando = false;

  @HostListener('window:resize')
  ajustarAltura(): void {
    if (!this.elementoRaiz) {
      return;
    }
    const topo = this.elementoRaiz.getBoundingClientRect().top + window.scrollY;
    this.alturaBancada = Math.max(560, window.innerHeight - topo - 16) + 'px';
    this.definirLarguraInspetor(this.larguraInspetor, false);
  }

  iniciarArraste(evento: PointerEvent): void {
    if (evento.button !== 0) {
      return;
    }
    (evento.currentTarget as HTMLElement).setPointerCapture(evento.pointerId);
    this.arrastando = true;
    evento.preventDefault();
  }

  arrastar(evento: PointerEvent): void {
    if (!this.arrastando || !this.bancada) {
      return;
    }
    const caixa = this.bancada.nativeElement.getBoundingClientRect();
    this.definirLarguraInspetor(caixa.right - evento.clientX - 4, false);
  }

  soltarArraste(evento: PointerEvent): void {
    if (!this.arrastando) {
      return;
    }
    this.arrastando = false;
    (evento.currentTarget as HTMLElement).releasePointerCapture(evento.pointerId);
    this.definirLarguraInspetor(this.larguraInspetor);
  }

  definirLarguraInspetor(largura: number, salvar = true): void {
    const total = this.bancada?.nativeElement.clientWidth || window.innerWidth;
    const maxima = Math.max(320, total - 420);
    this.larguraInspetor = Math.round(Math.min(maxima, Math.max(320, largura)));
    if (salvar) {
      try {
        localStorage.setItem('rd-fonte-inspetor', String(this.larguraInspetor));
      } catch (e) {}
    }
  }

  private larguraSalva(): number {
    try {
      const salva = Number(localStorage.getItem('rd-fonte-inspetor'));
      return salva > 0 ? salva : this.LARGURA_INSPETOR_PADRAO;
    } catch (e) {
      return this.LARGURA_INSPETOR_PADRAO;
    }
  }

  get titulo(): string {
    return this.fonte.titulo || (this.id ? this.fonte.chave : 'Nova fonte');
  }

  get rotuloSituacao(): string {
    if (!this.id) {
      return 'Nova, não salva';
    }
    if (!this.versaoAtual) {
      return 'Rascunho, sem versão';
    }
    return this.temRascunho ? `Rascunho sobre a v${this.versaoAtual}` : `Versão ${this.versaoAtual}`;
  }

  get proximaVersao(): number {
    return (this.versaoAtual || 0) + 1;
  }

  get parametrosNoSql(): string[] {
    const sql = (this.fonte.sql || '')
      .replace(/'(?:[^']|'')*'/g, "''")
      .replace(/--[^\n]*/g, '')
      .replace(/\/\*[\s\S]*?\*\//g, '');
    const nomes = new Set<string>();
    const re = /(^|[^:]):([a-zA-Z_][a-zA-Z0-9_]*)/g;
    let m: RegExpExecArray;
    while ((m = re.exec(sql))) {
      nomes.add(m[2].toLowerCase());
    }
    return Array.from(nomes);
  }

  get parametrosPrevia(): any[] {
    return this.prepararValores();
  }

  private prepararValores(): any[] {
    const lista = (this.fonte.parametros || []).filter((p: any) => p.nome && p.tipo && p.tipo !== 'empresas');
    for (const p of lista) {
      if (p.tipo === 'periodo' && this.valoresPrevia[p.nome + '__inicio'] === undefined) {
        const [inicio, fim] = this.periodoPadrao(p.padrao);
        this.valoresPrevia[p.nome + '__inicio'] = inicio;
        this.valoresPrevia[p.nome + '__fim'] = fim;
      } else if (p.tipo !== 'periodo' && this.valoresPrevia[p.nome] === undefined) {
        this.valoresPrevia[p.nome] = p.padrao ?? null;
      }
    }
    return lista;
  }

  get qtdeChecagens(): number {
    return (this.validacao?.erros?.length || 0) + (this.validacao?.avisos?.length || 0) + (this.plano?.avisos?.length || 0);
  }

  get temErro(): boolean {
    return !!this.validacao?.erros?.length || this.naoDeclarados.length > 0;
  }

  get ultimaExecucao(): string {
    if (this.ocupado) {
      return this.ocupado + '…';
    }
    if (!this.previa) {
      return 'prévia ainda não rodou';
    }
    return `${this.totalPrevia.toLocaleString('pt-BR')} linhas · ${(this.previa.duracaoMs / 1000).toFixed(1).replace('.', ',')} s`;
  }

  resumoValor(p: any): string {
    this.prepararValores();
    if (p.tipo === 'empresas') {
      return 'vem da base escolhida';
    }
    if (p.tipo === 'periodo') {
      const inicio = this.valoresPrevia[p.nome + '__inicio'];
      const fim = this.valoresPrevia[p.nome + '__fim'];
      return inicio && fim ? `${this.dataBr(inicio)} a ${this.dataBr(fim)}` : 'sem valor';
    }
    const valor = this.valoresPrevia[p.nome];
    return valor === null || valor === undefined || valor === '' ? 'sem valor' : String(valor);
  }

  alternarColuna(c: any): void {
    if (this.colunasAbertas.has(c)) {
      this.colunasAbertas.delete(c);
    } else {
      this.colunasAbertas.add(c);
    }
  }

  get naoDeclarados(): string[] {
    const esperados = new Set<string>(['empresas']);
    for (const p of this.fonte.parametros || []) {
      if (p.tipo === 'periodo') {
        esperados.add(p.nome + '_inicio');
        esperados.add(p.nome + '_fim');
      } else {
        esperados.add(p.nome);
      }
    }
    return this.parametrosNoSql.filter((n) => !esperados.has(n));
  }

  adicionarParametro(nome = ''): void {
    const periodo = nome.endsWith('_inicio') || nome.endsWith('_fim');
    this.fonte.parametros.push({
      nome: periodo ? nome.replace(/_(inicio|fim)$/, '') : nome,
      tipo: periodo ? 'periodo' : 'texto',
      rotulo: '',
      padrao: periodo ? 'mesAtual' : null,
    });
  }

  declararFaltantes(): void {
    const vistos = new Set<string>();
    for (const nome of this.naoDeclarados) {
      const base = nome.replace(/_(inicio|fim)$/, '');
      if (!vistos.has(base)) {
        vistos.add(base);
        this.adicionarParametro(nome);
      }
    }
  }

  definirOpcoes(p: any, texto: string): void {
    const opcoes = (texto || '')
      .split(',')
      .map((o) => o.trim())
      .filter((o) => o);
    p.opcoes = opcoes.length ? opcoes : null;
    if (p.opcoes && p.padrao && !p.opcoes.includes(p.padrao)) {
      p.padrao = p.opcoes[0];
    }
  }

  removerParametro(i: number): void {
    this.fonte.parametros.splice(i, 1);
  }

  adicionarColuna(): void {
    this.fonte.colunas.push({ nome: '', rotulo: '', tipo: 'texto' });
  }

  adicionarVariavel(): void {
    const numerica = (this.fonte.colunas || []).find((c: any) => ['moeda', 'decimal', 'inteiro', 'percentual'].includes(c.tipo));
    const nova = { nome: '', total: numerica ? 'soma' : 'contagem', coluna: numerica?.nome || this.fonte.colunas[0]?.nome || null };
    this.fonte.variaveis.push(nova);
    this.variaveisAbertas.add(nova);
  }

  removerVariavel(i: number): void {
    this.variaveisAbertas.delete(this.fonte.variaveis[i]);
    this.fonte.variaveis.splice(i, 1);
  }

  nomeTotal(total: string): string {
    return total ? NOMES_TOTAL[total] || total : '';
  }

  resumoVariavel(v: any): string {
    if (v.total) {
      const coluna = (this.fonte.colunas || []).find((c: any) => c.nome === v.coluna);
      return v.coluna ? `de ${coluna?.rotulo || v.coluna}` : 'escolha a coluna';
    }
    return v.escopo || '';
  }

  removerColuna(i: number): void {
    this.fonte.colunas.splice(i, 1);
  }

  validar(): void {
    this.executar('Validando', this.service.validarFonte(this.limpa()), (v) => {
      this.validacao = v;
      this.abaConsole = 1;
      if (v.valida) {
        this.messageService.add({ severity: 'success', summary: 'Fonte válida', detail: v.avisos?.length ? 'Confira os avisos.' : 'Nenhum problema encontrado.' });
      }
    });
  }

  detectarColunas(): void {
    this.executar('Detectando colunas', this.service.detectarColunas(this.limpa()), (r) => {
      const existentes = new Map<string, any>((this.fonte.colunas || []).map((c: any) => [c.nome, c]));
      const detectadas: any[] = r.colunas || [];
      const removidas = (this.fonte.colunas || []).filter((c: any) => !detectadas.some((d) => d.nome === c.nome)).map((c: any) => c.nome);
      this.fonte.colunas = detectadas.map((d) => {
        const atual = existentes.get(d.nome);
        if (atual) {
          return { ...atual, tipo: atual.tipo === 'moeda' || atual.tipo === 'percentual' ? atual.tipo : d.tipo };
        }
        return { nome: d.nome, rotulo: this.humanizar(d.nome), tipo: d.tipo };
      });
      this.abaInspetor = 2;
      this.messageService.add({
        severity: 'info',
        summary: 'Colunas detectadas',
        detail: `${detectadas.length} colunas em ${r.base}.` + (removidas.length ? ` Saíram: ${removidas.join(', ')}.` : ''),
        life: 6000,
      });
    });
  }

  get totalPrevia(): number {
    if (!this.previa) {
      return 0;
    }
    if (this.previa.total >= 0) {
      return this.previa.total;
    }
    return (this.previa.deslocamento || 0) + (this.previa.linhas?.length || 0) + (this.previa.truncado ? 1 : 0);
  }

  rodarPrevia(): void {
    this.carregarPaginaPrevia(0, this.porPaginaPrevia);
  }

  trocarPaginaPrevia(evento: { first?: number; rows?: number }): void {
    const porPagina = evento.rows || this.porPaginaPrevia;
    this.carregarPaginaPrevia(Math.floor((evento.first || 0) / porPagina), porPagina);
  }

  private carregarPaginaPrevia(pagina: number, porPagina: number): void {
    this.executar('Rodando prévia', this.service.previaFonte(this.limpa(), porPagina, this.filtrosPrevia(), pagina), (r) => {
      this.previa = r;
      this.colunasPrevia = r.colunas || [];
      this.porPaginaPrevia = porPagina;
      this.primeiraPrevia = pagina * porPagina;
      this.abaConsole = 0;
    });
  }

  rodarPlano(): void {
    this.executar('Gerando plano', this.service.planoExecucao(this.limpa()), (r) => {
      this.plano = r;
      this.abaConsole = 1;
    });
  }

  salvarRascunho(): void {
    this.executar('Salvando rascunho', this.garantirSalva(true), () =>
      this.messageService.add({ severity: 'success', summary: 'Rascunho salvo', detail: 'Nada muda nos clientes até a fonte virar versão e o modelo ser publicado.' })
    );
  }

  abrirSalvarVersao(): void {
    this.notasVersao = '';
    this.mostrarSalvarVersao = true;
  }

  salvarVersao(): void {
    this.mostrarSalvarVersao = false;
    const salvar$ = this.garantirSalva(false);
    this.ocupado = 'Salvando versão';
    salvar$.subscribe(
      () =>
        this.service.salvarVersaoFonte(this.id, this.limpa(), this.notasVersao).subscribe(
          (r) => {
            this.ocupado = '';
            this.aplicar(r);
            this.validacao = { erros: [], avisos: r.avisos || [] };
            this.messageService.add({
              severity: 'success',
              summary: `Versão ${r.versaoAtual} salva`,
              detail: this.modelos.length ? `Os ${this.modelos.length} modelos seguem na versão congelada até serem republicados.` : '',
              life: 6000,
            });
          },
          (e) => this.falha(e)
        ),
      (e) => this.falha(e)
    );
  }

  descartarRascunho(): void {
    this.confirmationService.confirm({
      message: `Descartar o rascunho e voltar para a versão ${this.versaoAtual}?`,
      accept: () => this.executar('Descartando', this.service.descartarRascunhoFonte(this.id), (r) => this.aplicar(r)),
    });
  }

  excluir(): void {
    this.confirmationService.confirm({
      message: this.versaoAtual ? 'Excluir esta fonte e todas as versões? Não dá para desfazer.' : 'Excluir esta fonte? Ela ainda não tem versão salva.',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.executar('Excluindo', this.service.excluirFonte(this.id), () => this.voltar()),
    });
  }

  abrirHistorico(): void {
    this.versaoComparada = null;
    this.executar('Carregando histórico', this.service.versoesFonte(this.id), (v) => {
      this.versoes = v;
      this.mostrarHistorico = true;
      if (v.length) {
        this.comparar(v[0]);
      }
    });
  }

  comparar(versao: any): void {
    this.service.versaoFonte(this.id, versao.versao).subscribe(
      (v) => (this.versaoComparada = v),
      (e) => this.falha(e)
    );
  }

  restaurar(versao: any): void {
    this.fonte = this.normalizar(versao.fonte);
    this.mostrarHistorico = false;
    this.messageService.add({ severity: 'info', summary: `Versão ${versao.versao} carregada no editor`, detail: 'Salve o rascunho ou uma nova versão para manter.' });
  }

  get resumoSugestao(): string[] {
    const s = this.sugestaoModelo;
    if (!s) {
      return [];
    }
    const rotulo = (nome: string) => this.fonte.colunas.find((c: any) => c.nome === nome)?.rotulo || nome;
    const totais = (s.colunas || []).filter((c: any) => c.total);
    const variaveis = (s.variaveis || []).map((v: any) => v.variavel);
    const linhas = [`${(s.colunas || []).length} colunas e ${(s.filtros || []).length} filtros`];
    if ((s.agrupamentos || []).length) {
      linhas.push('Agrupado por ' + s.agrupamentos.map((a: any) => rotulo(a.coluna)).join(' › '));
    }
    if (totais.length) {
      linhas.push('Totais: ' + totais.map((c: any) => `${c.total} de ${rotulo(c.coluna)}`).join(', '));
    }
    if (variaveis.length) {
      linhas.push('Variáveis: ' + variaveis.join(', '));
    }
    if ((s.indicadores || []).length) {
      linhas.push('Indicadores: ' + s.indicadores.map((i: any) => i.rotulo).join(', '));
    }
    return linhas;
  }

  lerJrxml(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (!arquivo) {
      return;
    }
    this.executar('Lendo JRXML', this.service.importarJrxml(arquivo), (importacao) => {
      if (!this.id && !(this.fonte.sql || '').trim()) {
        this.fonte = this.normalizar(importacao.fonte);
      }
      this.mostrarImportacao(importacao);
    });
  }

  private mostrarImportacao(importacao: ImportacaoJrxml): void {
    this.avisosImportacao = importacao.avisos || [];
    this.origemImportacao = importacao.origem;
    this.sugestaoModelo = importacao.sugestaoModelo || null;
    const nomes = new Set((this.fonte.variaveis || []).map((v: any) => v.nome));
    this.fonte.variaveis = [...(this.fonte.variaveis || []), ...(importacao.variaveis || []).filter((v: any) => !nomes.has(v.nome)).map((v: any) => ({ ...v }))];
    this.abaConsole = 2;
  }

  criarModeloSugerido(): void {
    if (!this.id || !this.versaoAtual) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Salve uma versão da fonte antes de criar o modelo.' });
      return;
    }
    const s = this.sugestaoModelo;
    const colunas = new Set(this.fonte.colunas.map((c: any) => c.nome));
    const parametros = new Set(this.fonte.parametros.map((p: any) => p.nome));
    const variaveis = new Set((this.fonte.variaveis || []).filter((v: any) => v.total && v.coluna).map((v: any) => v.nome));
    const modelo: any = {
      ...s,
      fonte: this.fonte.chave,
      filtros: (s.filtros || []).filter((f: any) => parametros.has(f.parametro)),
      colunas: (s.colunas || []).filter((c: any) => colunas.has(c.coluna)),
      agrupamentos: (s.agrupamentos || []).filter((a: any) => colunas.has(a.coluna)),
      indicadores: (s.indicadores || []).filter((i: any) => (i.variavel ? variaveis.has(i.variavel) : !i.metrica?.coluna || colunas.has(i.metrica.coluna))),
      variaveis: (s.variaveis || []).filter((v: any) => variaveis.has(v.variavel)),
    };
    if (!modelo.colunas.length) {
      modelo.colunas = this.fonte.colunas.map((c: any) => ({ coluna: c.nome }));
    }
    this.executar('Criando modelo', this.service.criarModelo(modelo, null), (r: any) => {
      this.sugestaoModelo = null;
      this.router.navigate(['relatorios-dinamicos/modelo'], { queryParams: { id: r.id } });
    });
  }

  abrirModelo(m: any): void {
    this.router.navigate(['relatorios-dinamicos/modelo'], { queryParams: { id: m.id } });
  }

  voltar(): void {
    this.router.navigate(['relatorios-dinamicos']);
  }

  private filtrosPrevia(): any {
    const filtros: any = {};
    for (const p of this.parametrosPrevia) {
      if (p.tipo === 'periodo') {
        const inicio = this.valoresPrevia[p.nome + '__inicio'];
        const fim = this.valoresPrevia[p.nome + '__fim'];
        if (inicio && fim) {
          filtros[p.nome] = { inicio, fim };
        }
        continue;
      }
      const valor = this.valoresPrevia[p.nome];
      if (valor === null || valor === undefined || valor === '') {
        continue;
      }
      filtros[p.nome] = p.tipo.startsWith('lista')
        ? String(valor).split(',').map((v) => v.trim()).filter((v) => v)
        : valor;
    }
    return filtros;
  }

  private periodoPadrao(padrao: string): [string, string] {
    const hoje = new Date();
    const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().substring(0, 10);
    const dia = (ano: number, mes: number, d: number) => new Date(ano, mes, d);
    const a = hoje.getFullYear();
    const m = hoje.getMonth();
    switch (padrao) {
      case 'hoje':
        return [iso(hoje), iso(hoje)];
      case 'ontem':
        return [iso(dia(a, m, hoje.getDate() - 1)), iso(dia(a, m, hoje.getDate() - 1))];
      case 'mesAnterior':
        return [iso(dia(a, m - 1, 1)), iso(dia(a, m, 0))];
      case 'ultimos7':
        return [iso(dia(a, m, hoje.getDate() - 6)), iso(hoje)];
      case 'ultimos30':
        return [iso(dia(a, m, hoje.getDate() - 29)), iso(hoje)];
      case 'anoAtual':
        return [iso(dia(a, 0, 1)), iso(dia(a, 11, 31))];
      default:
        return [iso(dia(a, m, 1)), iso(dia(a, m + 1, 0))];
    }
  }

  private garantirSalva(forcarRascunho: boolean): Observable<any> {
    if (!this.id) {
      return this.service.criarFonte(this.limpa()).pipe(
        map((r) => {
          this.aplicar(r);
          this.router.navigate([], { queryParams: { id: r.id }, replaceUrl: true });
          return r;
        })
      );
    }
    if (!forcarRascunho) {
      return of(null);
    }
    return this.service.salvarRascunhoFonte(this.id, this.limpa()).pipe(map((r) => this.aplicar(r)));
  }

  private carregar(id: number): void {
    this.executar('Carregando', this.service.fonte(id), (r) => this.aplicar(r));
  }

  private aplicar(r: any): any {
    this.id = r.id;
    this.versaoAtual = r.versaoAtual || null;
    this.temRascunho = !!r.temRascunho;
    this.modelos = r.modelos || [];
    this.fonte = this.normalizar(r.fonte);
    this.breadcrumb();
    return r;
  }

  private executar<T>(rotulo: string, obs: Observable<T>, ok: (r: T) => void): void {
    this.ocupado = rotulo;
    obs.subscribe(
      (r) => {
        this.ocupado = '';
        ok(r);
      },
      (e) => this.falha(e)
    );
  }

  private falha(e: any): void {
    this.ocupado = '';
    const corpo = this.service.corpoErro(e);
    if (corpo?.erros) {
      this.validacao = { erros: corpo.erros, avisos: corpo.avisos || [] };
    }
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.service.mensagemErro(e), life: 10000 });
  }

  private limpa(): any {
    const f = JSON.parse(JSON.stringify(this.fonte));
    for (const chave of ['descricao', 'software', 'rotinaExigida']) {
      if (!f[chave]) {
        delete f[chave];
      }
    }
    if (!f.semEmpresa) {
      delete f.semEmpresa;
    }
    f.parametros = (f.parametros || []).map((p: any) => this.semVazios(p));
    f.colunas = (f.colunas || []).map((c: any) => this.semVazios(c));
    f.variaveis = (f.variaveis || []).map((v: any) => this.semVazios(v));
    return f;
  }

  private semVazios(o: any): any {
    const r: any = {};
    for (const [k, v] of Object.entries(o)) {
      if (v !== null && v !== undefined && v !== '' && v !== false) {
        r[k] = v;
      }
    }
    return r;
  }

  private normalizar(f: any): any {
    const fonte = { ...this.fonteVazia(), ...(f || {}) };
    fonte.parametros = fonte.parametros || [];
    fonte.colunas = fonte.colunas || [];
    fonte.variaveis = fonte.variaveis || [];
    return fonte;
  }

  private fonteVazia(): any {
    return {
      chave: '',
      titulo: '',
      descricao: '',
      software: 'HABIL_10',
      rotinaExigida: null,
      semEmpresa: false,
      sql: SQL_INICIAL,
      parametros: [
        { nome: 'empresas', tipo: 'empresas' },
        { nome: 'periodo', tipo: 'periodo', rotulo: 'Emissão', padrao: 'mesAtual' },
      ],
      colunas: [],
      variaveis: [],
    };
  }

  private dataBr(iso: string): string {
    const [a, m, d] = String(iso).split('-');
    return d ? `${d}/${m}/${a}` : iso;
  }

  private humanizar(nome: string): string {
    const s = nome.replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private breadcrumb(): void {
    this.breadcrumbService.setItems([
      { label: 'Relatórios dinâmicos', routerLink: '/relatorios-dinamicos' },
      { label: 'Fontes SQL' },
      { label: this.titulo },
    ]);
  }
}
